import { BadgeCheck, CalendarClock, Clock, ShieldCheck, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import type { Author, Reviewer } from './authors';

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/* ────────────────────────────────────────────────────────────
   Compact byline — sits directly under the article headline
   ──────────────────────────────────────────────────────────── */

interface BylineProps {
  author: Author;
  updatedAt: string;
  readingTimeMinutes: number;
  factChecked?: boolean;
  className?: string;
}

export function AuthorByline({
  author,
  updatedAt,
  readingTimeMinutes,
  factChecked,
  className,
}: BylineProps) {
  return (
    <div
      className={twMerge(
        'flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-ink-200 py-4 dark:border-surface-dark-border',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-jeok-500 text-sm font-bold text-white dark:bg-jeok-dark">
          {author.avatar ? (
            <img src={author.avatar} alt="" className="h-full w-full rounded-full object-cover" />
          ) : (
            author.name.slice(0, 1)
          )}
        </span>
        <div className="leading-tight">
          <Link
            to={author.profileUrl}
            className="text-sm font-semibold text-ink-900 hover:underline dark:text-ink-50"
          >
            {author.name}
          </Link>
          <p className="text-xs text-ink-500 dark:text-ink-400">{author.title}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-500 dark:text-ink-400">
        <span className="inline-flex items-center gap-1.5">
          <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
          Updated {formatDate(updatedAt)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          {readingTimeMinutes} min read
        </span>
        {factChecked && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 font-medium text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Fact-checked
          </span>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Full E-E-A-T block — sits at the foot of the article
   ──────────────────────────────────────────────────────────── */

export interface Source {
  label: string;
  url: string;
  /** e.g. "Korea Immigration Service", "IRS" — names the authority. */
  publisher: string;
}

interface EEATBlockProps {
  author: Author;
  reviewer?: Reviewer;
  publishedAt: string;
  updatedAt: string;
  lastVerifiedAt: string;
  /** Gates the "Facts last verified" claim — see the ledger below. */
  factChecked?: boolean;
  sources: Source[];
  /** YMYL disclaimer. Money/visa guides must carry one. */
  disclaimer?: string;
  className?: string;
}

export function EEATBlock({
  author,
  reviewer,
  publishedAt,
  updatedAt,
  lastVerifiedAt,
  factChecked = false,
  sources,
  disclaimer,
  className,
}: EEATBlockProps) {
  return (
    <section
      className={twMerge('mt-16 space-y-6', className)}
      aria-label="About this guide"
    >
      {/* Author bio card */}
      <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card">
        <div className="h-1 w-full bg-jeok-500 dark:bg-jeok-dark" />
        <div className="p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
            About the author
          </p>

          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-jeok-500 text-base font-bold text-white dark:bg-jeok-dark">
              {author.avatar ? (
                <img src={author.avatar} alt="" className="h-full w-full rounded-full object-cover" />
              ) : (
                author.name.slice(0, 1)
              )}
            </span>

            <div className="min-w-0">
              <h3 className="text-base font-bold text-ink-900 dark:text-ink-50">
                {author.name}
                {author.nameKo && (
                  <span lang="ko" className="ml-2 font-korean text-sm font-medium text-ink-500 dark:text-ink-400">
                    {author.nameKo}
                  </span>
                )}
              </h3>
              <p className="text-xs text-ink-500 dark:text-ink-400">{author.title}</p>
              <p className="mt-2 text-sm leading-6 text-ink-700 dark:text-ink-200">{author.bio}</p>

              {author.credentials.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {author.credentials.map((c) => (
                    <li
                      key={c}
                      className="flex items-start gap-2 text-xs leading-relaxed text-ink-500 dark:text-ink-400"
                    >
                      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-cheong-500 dark:text-cheong-dark" aria-hidden="true" />
                      {c}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Verification ledger */}
      <div className="rounded-xl border border-ink-200 bg-ink-50 p-5 dark:border-surface-dark-border dark:bg-white/[0.03]">
        <div className="mb-4 flex items-center gap-2">
          <BadgeCheck className="h-4 w-4 text-green-600 dark:text-green-500" aria-hidden="true" />
          <h3 className="text-sm font-bold text-ink-900 dark:text-ink-50">How this guide is maintained</h3>
        </div>

        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-ink-500 dark:text-ink-400">First published</dt>
            <dd className="font-medium text-ink-900 dark:text-ink-50">{formatDate(publishedAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-500 dark:text-ink-400">Last updated</dt>
            <dd className="font-medium text-ink-900 dark:text-ink-50">{formatDate(updatedAt)}</dd>
          </div>
          {/*
            "Facts last verified" is a claim that a person checked the figures.
            Only make it when factChecked is true — otherwise the date is the
            generation date, and printing it under that label asserts a review
            that never happened. Unverified guides simply omit the row.
          */}
          {factChecked ? (
            <div>
              <dt className="text-xs text-ink-500 dark:text-ink-400">Facts last verified</dt>
              <dd className="font-medium text-ink-900 dark:text-ink-50">{formatDate(lastVerifiedAt)}</dd>
            </div>
          ) : (
            <div>
              <dt className="text-xs text-ink-500 dark:text-ink-400">Independent fact-check</dt>
              <dd className="font-medium text-ink-700 dark:text-ink-200">Not yet completed</dd>
            </div>
          )}
        </dl>

        {reviewer && (
          <div className="mt-4 border-t border-ink-200 pt-4 dark:border-surface-dark-border">
            <p className="text-sm text-ink-700 dark:text-ink-200">
              <span className="font-semibold text-ink-900 dark:text-ink-50">
                Reviewed by {reviewer.name}
              </span>
              <span className="text-ink-500 dark:text-ink-400"> · {reviewer.title}</span>
            </p>
            <p className="mt-1 text-xs leading-relaxed text-ink-500 dark:text-ink-400">{reviewer.scope}</p>
          </div>
        )}

        {sources.length > 0 && (
          <div className="mt-4 border-t border-ink-200 pt-4 dark:border-surface-dark-border">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
              Primary sources
            </p>
            <ul className="space-y-1.5">
              {sources.map((s) => (
                <li key={s.url} className="text-sm leading-relaxed">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-baseline gap-1 font-medium text-cheong-500 hover:underline dark:text-cheong-dark"
                  >
                    {s.label}
                    <ExternalLink className="h-3 w-3 flex-shrink-0 self-center" aria-hidden="true" />
                  </a>
                  <span className="text-ink-500 dark:text-ink-400"> — {s.publisher}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {disclaimer && (
        <p className="rounded-lg border border-ink-200 bg-white px-4 py-3 text-xs leading-relaxed text-ink-500 dark:border-surface-dark-border dark:bg-surface-dark-card dark:text-ink-400">
          {disclaimer}
        </p>
      )}
    </section>
  );
}
